import { Form, Formik } from 'formik'
import { Dispatch, SetStateAction, useEffect, useState, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import { useAuth } from '../../auth'
import {
  createDocumentSubmission,
  getTemplateApi,
  getTemplateByDocusealIdApi,
  getTemplateTokenApi,
} from '../core/_requests'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { toast } from 'react-toastify'
import DocumentGeneralDetails from './DocumentGeneralDetails'
import Signatories from './Signatories'
import { InfoCard2 } from '../../widgets/components/UI/InfoCard2'
import DocumentPreview from './DocumentPreview'
import { getInputDueDate } from '../core/_utils'
import { DocumentFormValues, TemplateDetails } from '../core/_models'
import { formatDate, getTodayDate, removeNullFields } from '../../../core/_util'
import DocusealTemplate from './DocusealTemplate'
import Skeleton from 'react-loading-skeleton'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { useThemeMode } from '../../../../_metronic/partials'
import { isMobile } from 'react-device-detect'
import { errorMessage } from '../../profile/core/_constants'
import brandingConfig from '../../../../config/brandingConfig'

const CreateDocumentModal = ({
  showModal,
  setShowModal,
  tabKey,
  currentDocumentStep,
  setCurrentDocumentStep,
  setIsDocumentCreate,
  setShowTemplateModal,
  templateDocusealId,
  setIsTemplateVisible,
  isUpdateTemplateDropdown,
  submitterDetails,
  getDocumentsDashboardCounts,
  templateId,
  setTemplateId,
  viewDocumentLoading,
  setSubmitterDetails,
  isDuplicateDocument,
}: {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  tabKey: number
  currentDocumentStep: number
  setCurrentDocumentStep: Dispatch<SetStateAction<number>>
  setIsDocumentCreate: Dispatch<SetStateAction<boolean>>
  setShowTemplateModal: Dispatch<SetStateAction<boolean>>
  templateDocusealId: number | undefined
  setIsTemplateVisible: Dispatch<SetStateAction<boolean>>
  isUpdateTemplateDropdown: boolean
  submitterDetails: any
  getDocumentsDashboardCounts: () => {}
  templateId: number | undefined
  setTemplateId: Dispatch<SetStateAction<number | undefined>>
  viewDocumentLoading: boolean
  setSubmitterDetails: Dispatch<SetStateAction<any>>
  isDuplicateDocument: boolean
}) => {
  const { formatMessage } = useIntl()

  const [loading, setLoading] = useState(false)
  const { currentUser, companyId, setShowBillingModal } = useAuth()

  const formikRef = useRef<any>(null)
  const [totalSteps] = useState(4)

  const [error, setError] = useState()
  const [signatoryRole, setSignatoryRole] = useState<string>()
  const [isEdited, setIsEdited] = useState(false)
  const [templateDetails, setTemplateDetails] = useState<TemplateDetails | undefined>()
  const [documentTemplateToken, setDocumentTemplateToken] = useState('')
  const [isInValidTemplate, setIsInValidTemplate] = useState(false)
  const [documentDocusealId, setDocumentDocusealId] = useState<number | undefined>()
  const { mode } = useThemeMode()
  const validationSchema = [
    Yup.object({
      title: Yup.string()
        .max(50, formatMessage({ id: 'Maximum 50 characters' }))
        .required(formatMessage({ id: 'Template Name is required' })),
      expiryDate: Yup.date()
        .min(getTodayDate(), formatMessage({ id: 'Expiry Date cannot be in the past' }))
        .nullable(),
      templateId: Yup.object()
        .shape({
          id: Yup.string().required(formatMessage({ id: 'Template ID is required' })),
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .nullable(),
    }),
    Yup.object({
      signatories: Yup.array().of(
        Yup.object({
          name: Yup.string().required(formatMessage({ id: 'Name is required' })),
          email: Yup.string()
            .email(formatMessage({ id: 'Invalid email' }))
            .required(formatMessage({ id: 'Email is required' })),
          dueDate: Yup.date()
            .required(formatMessage({ id: 'Due Date is required' }))
            .min(getTodayDate(), formatMessage({ id: 'Due Date cannot be in the past' }))
            .test(
              'dueDate-before-expiryDate',
              formatMessage({ id: 'Due Date cannot be later than Expiry Date' }),
              function (value) {
                if (formikRef.current && formikRef.current.values.expiryDate) {
                  return (
                    new Date(value!).setHours(0, 0, 0, 0) <=
                    new Date(formikRef.current.values.expiryDate).setHours(0, 0, 0, 0)
                  )
                }
                return true
              }
            ),
        })
      ),
    }),
  ]
  const [currentSchema, setCurrentSchema] = useState(validationSchema[0])

  let initialValues = {
    title: submitterDetails?.title ? `${submitterDetails?.title} Copy` : '',
    expiryDate: submitterDetails?.expiryDate ? formatDate(submitterDetails?.expiryDate) : '',
    templateId:
      submitterDetails && submitterDetails.signing_template
        ? {
          id: submitterDetails.signing_template.templateId,
          label: submitterDetails.signing_template.title,
          value: submitterDetails.signing_template.templateId,
        }
        : null,
    signatories:
      submitterDetails?.document_signatories.length > 0
        ? submitterDetails?.document_signatories.map(
          ({
            signatoryName,
            signatoryRole,
            signatoryEmail,
            dueDate,
          }: {
            signatoryName: string
            signatoryRole: string
            signatoryEmail: string
            dueDate: Date
          }) => ({
            role: signatoryRole,
            name: signatoryName,
            email: signatoryEmail,
            dueDate: getInputDueDate(dueDate),
          })
        )
        : [{ role: 'First Party', name: '', email: '', dueDate: '' }],
  }

  useEffect(() => {
    if (templateId) {
      getTemplate()
    }
  }, [templateId]) // eslint-disable-line react-hooks/exhaustive-deps

  const getTemplate = async () => {
    try {
      const {
        data: { data },
      } = await getTemplateApi(templateId!, companyId!)

      if (data) {
        setTemplateDetails(data.templateDetails)
        if (formikRef.current) {
          formikRef.current.setFieldValue('signatories', data.submitters)
        }
      } else {
        toast.error(formatMessage({ id: data.errors[0] }))

        if (formikRef.current) {
          formikRef.current.setFieldValue('signatories', [])
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  const getTemplateByDocusealId = async () => {
    setLoading(true)
    try {
      const {
        data: { data },
      } = await getTemplateByDocusealIdApi(documentDocusealId!, companyId!)

      if (data) {
        setTemplateDetails(data.templateDetails)
        if (formikRef.current) {
          formikRef.current.setFieldValue('signatories', data.submitters)
        }
      } else {
        toast.error(formatMessage({ id: data.errors[0] }))

        if (formikRef.current) {
          formikRef.current.setFieldValue('signatories', [])
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (formValues: DocumentFormValues) => {
    setLoading(true)
    let values = {
      ...formValues,
      templateId: formValues?.templateId?.value,
      docusealId: documentDocusealId,
      companyId,
    }

    try {
      const {
        data: { success, errors },
      } = await createDocumentSubmission(values)
      if (success) {
        toast.success(formatMessage({ id: 'Document submitted for signatures' }))
        setIsDocumentCreate(true)
        setShowModal(false)
        setDocumentDocusealId(undefined)
        setSignatoryRole(undefined)
        setIsEdited(false)
        await getDocumentsDashboardCounts()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningDocumentCreatedSuccessful,
            metadata: removeNullFields({
              document_name: values.title,
              expiry_date: values.expiryDate,
              signers_count: values.signatories.length,
              creator_name: `${currentUser?.firstName} ${currentUser?.lastName}`,
              creator_email: currentUser?.email,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
            }),
          },
        })
      } else {
        setError(errors[0])
        errors.forEach((error: string) => {
          if (
            error === errorMessage.UPGRADE_BILLING_PLAN_LIMIT_EXCEEDED ||
            error === errorMessage.UPGRADE_BILLING_PLAN_FEATURE
          ) {
            setShowBillingModal(true)
            toast.error(formatMessage({ id: error }), { autoClose: false })
          } else {
            toast.error(formatMessage({ id: error }))
          }
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningDocumentCreatedFailed,
            metadata: removeNullFields({
              document_name: values.title,
              expiry_date: values.expiryDate,
              signers_count: values.signatories.length,
              creator_name: `${currentUser?.firstName} ${currentUser?.lastName}`,
              creator_email: currentUser?.email,
              company_id: companyId,
              error: errors[0],
            }),
          },
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleTemplateCreate = async (templateName: string) => {
    setLoading(true)
    try {
      const {
        data: {
          data: { token },
        },
      } = await getTemplateTokenApi(false, templateName, 'NDA', companyId, undefined, false)
      if (token) {
        setDocumentTemplateToken(token)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const nextStep = async (formik: any) => {
    if (formik.isValid) {
      if (currentDocumentStep === 1 && !templateId && !documentDocusealId) {
        await handleTemplateCreate(`Default Template`)
      }
      if (currentDocumentStep === 2) {
        await getTemplateByDocusealId()
      }
      if (currentDocumentStep === 3) {
        const errors = await formik.validateForm()
        if (Object.keys(errors).length) {
          formik.setFieldErrors('signatories', errors)
        }
      }
      if (currentDocumentStep !== totalSteps) {
        setCurrentDocumentStep((prev) => {
          if (templateId) {
            setCurrentSchema(validationSchema[prev])
          } else {
            setCurrentSchema(validationSchema[prev - 1])
          }
          if (currentDocumentStep === 1) {
            return templateId ? prev + 2 : prev + 1
          }

          return prev + 1
        })
      } else {
        await formik.submitForm()
        if (error) {
          toast.error(formatMessage({ id: error }))
          setError(undefined)
          return
        }
        if (formikRef.current) {
          formikRef.current.resetForm()
        }
      }
    }
  }

  const prevStep = () => {
    if (currentDocumentStep === 3) {
      if (formikRef.current) {
        formikRef.current.setErrors({})
      }
    }
    if (currentDocumentStep !== 1) {
      setCurrentDocumentStep((prev) => {
        if ((templateId && prev - 2 === 1) || (!templateId && prev - 1 === 1)) {
          setCurrentSchema(validationSchema[0])
          setIsInValidTemplate(false)
          if (formikRef.current) {
            formikRef.current.setErrors({})
          }
        }
        if (prev === 3) {
          return templateId ? prev - 2 : prev - 1
        }
        return prev - 1
      })
    }
  }

  return (
    <Modal
      show={showModal}
      size='xl'
      onHide={() => {
        if (formikRef.current) {
          setCurrentSchema(validationSchema[0])
          formikRef.current.resetForm()
        }
        setCurrentDocumentStep(1)
        setDocumentDocusealId(undefined)
        setTemplateId(undefined)
        setShowModal(false)
        setSubmitterDetails(undefined)
      }}
      aria-labelledby='contained-modal-title-vcenter'
      centered
      className='template-modal-container'
    >
      <Modal.Header>
        <div className='w-100 d-flex justify-content-between align-items-center'>
          <div className='d-flex'>
            <div
              className='d-flex align-items-center cursor-pointer'
              onClick={() => {
                if (formikRef.current) {
                  setCurrentSchema(validationSchema[0])
                  formikRef.current.resetForm()
                }
                setSubmitterDetails(undefined)
                setDocumentDocusealId(undefined)
                setTemplateId(undefined)
                setCurrentDocumentStep(1)
                setShowModal(false)
              }}
            >
              <KTSVG
                path={
                  mode === 'dark'
                    ? toAbsoluteUrl('/media/icons/digital-signing/ic_close_dark.svg')
                    : toAbsoluteUrl('/media/icons/digital-signing/ic_close.svg')
                }
              />
            </div>
            <div className={`ps-4 ${isMobile ? 'font-size-13' : 'font-size-20'} fw-bold`}>
              {formatMessage({ id: 'Add New Document for Digital Signing' })}
            </div>
          </div>
          <ToolTipUIForModal
            tooltipText={formatMessage({
              id: 'Add and upload new documents for easy sharing and compliance',
            })}
          />
        </div>
      </Modal.Header>
      <Formik
        initialValues={initialValues}
        validationSchema={currentSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
        enableReinitialize
        validateOnMount
      >
        {(formik: any) => {
          return (
            <Form>
              <Modal.Body>
                <div
                  className='stepper stepper-pills stepper-column d-flex flex-column container'
                  id='kt_create_account_stepper'
                >
                  {currentDocumentStep !== 2 && (
                    <>
                      <div className='card d-flex justify-content-start justify-content-xl-start flex-row-auto w-100 overflow-mobile-x  me-9 '>
                        <div className='card-body  py-20 px-0 d-flex justify-content-start'>
                          <div className='stepper-nav'>
                            <div className='stepper-item current custom-marign-right'>
                              <div className='stepper-wrapper'>
                                <div className='stepper-icon w-40px h-40px'>
                                  <i className='stepper-check'></i>
                                  <span className='stepper-number'>1</span>
                                </div>
                                <div className='stepper-label'>
                                  <h3 className='stepper-title fw-semibold font-size-13'>
                                    {formatMessage({ id: 'General Details' })}
                                  </h3>

                                  <div className='stepper-desc font-size-12'>
                                    {formatMessage({ id: 'Basic Documents Details' })}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              className={`stepper-item ${currentDocumentStep === 3 || currentDocumentStep === 4
                                ? 'current'
                                : ''
                                } custom-marign-right`}
                            >
                              <div className='stepper-wrapper'>
                                <div className='stepper-icon w-40px h-40px'>
                                  <i className='stepper-check '></i>
                                  <span className='stepper-number'>2</span>
                                </div>
                                <div className='stepper-label'>
                                  <h3 className='stepper-title fw-semibold font-size-13'>
                                    {formatMessage({ id: 'Add Signatories' })}
                                  </h3>
                                  <div className='stepper-desc font-size-12'>
                                    {formatMessage({ id: 'Add people who are going to sign' })}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              className={`stepper-item ${currentDocumentStep === 4 ? 'current' : ''
                                }`}
                              data-kt-stepper-element='nav'
                            >
                              <div className='stepper-wrapper'>
                                <div className='stepper-icon w-40px h-40px'>
                                  <i className='stepper-check '></i>
                                  <span className='stepper-number'>3</span>
                                </div>

                                <div className='stepper-label'>
                                  <h3 className='stepper-title fw-semibold font-size-13'>
                                    {formatMessage({ id: 'Preview & Send' })}
                                  </h3>
                                  <div className='stepper-desc font-size-12'>
                                    {formatMessage({ id: 'Review everything and send' })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <InfoCard2
                        title={formatMessage({
                          id: 'Adding New Document for Digital Signing Made Easy',
                        })}
                        desc={formatMessage({
                          id: 'Easily upload new documents for digital signing, streamlining the signature process and ensuring timely execution of important agreements.',
                        })}
                        slug={brandingConfig.helpLinks.digitalSigningCreateDocument}
                        className='w-100 mb-0'
                        toolTipText={formatMessage({
                          id: 'Learn to upload a new document that requires digital signatures',
                        })}
                      />
                    </>
                  )}

                  <div className='pt-8 d-flex flex-column justify-content-between'>
                    <div className=''>
                      {currentDocumentStep === 1 && (
                        <div className='current w-100'>
                          {viewDocumentLoading ? (
                            <div>
                              <div className='d-flex justify-content-between gap-2 flex-wrap'>
                                <div className='d-flex flex-column'>
                                  <Skeleton
                                    width={150}
                                    height={15}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    className='mb-2'
                                  />
                                  <Skeleton
                                    width='45vw'
                                    height={40}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                                <div className='d-flex flex-column'>
                                  <Skeleton
                                    width={150}
                                    height={15}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    className='mb-2'
                                  />
                                  <Skeleton
                                    width='45vw'
                                    height={40}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                              <div className='d-flex justify-content-between gap-2 mt-8'>
                                <div className='d-flex flex-column'>
                                  <Skeleton
                                    width={150}
                                    height={15}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                    className='mb-2'
                                  />
                                  <Skeleton
                                    width='45vw'
                                    height={40}
                                    baseColor='var(--kt-skeleton-bg-color)'
                                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <DocumentGeneralDetails
                              setTemplateId={setTemplateId}
                              formik={formik}
                              showModal={showModal}
                              templateDocusealId={templateDocusealId}
                              isUpdateTemplateDropdown={isUpdateTemplateDropdown}
                              isDuplicateDocument={isDuplicateDocument}
                            />
                          )}
                        </div>
                      )}
                      {currentDocumentStep === 2 && (
                        <div className='current w-100'>
                          <DocusealTemplate
                            token={documentTemplateToken}
                            setIsInValidTemplate={setIsInValidTemplate}
                            setDocusealId={setDocumentDocusealId}
                          />
                        </div>
                      )}
                      {currentDocumentStep === 3 && (
                        <div className='current w-100'>
                          <p className='font-weight-500'>
                            {formatMessage({
                              id: 'Please add the signatories who will be signing this Document',
                            })}
                          </p>
                          <Signatories
                            formik={formik}
                            signatoryRole={signatoryRole}
                            isEdited={isEdited}
                          />
                        </div>
                      )}
                      {currentDocumentStep === 4 && (
                        <div className='current w-100'>
                          <DocumentPreview
                            formik={formik}
                            templateDetails={templateDetails}
                            setCurrentDocumentStep={setCurrentDocumentStep}
                            setSignatoryRole={setSignatoryRole}
                            setIsEdited={setIsEdited}
                            templateId={templateId}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className='border border-top-0 border-bottom-0 w-full-mobile'>
                {isMobile ? (
                  <div className='d-md-flex justify-content-md-end flex-wrap flex-nowrap w-full-mobile'>
                    <div className='w-full-mobile'>
                      <button
                        type='button'
                        className='btn btn-lg btn-primary w-full-mobile'
                        disabled={!formik.isValid || loading || isInValidTemplate}
                        onClick={() => {
                          nextStep(formik)
                        }}
                      >
                        {loading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label'>
                            {currentDocumentStep === 1 || currentDocumentStep === 2
                              ? formatMessage({ id: 'Next' })
                              : currentDocumentStep === 3
                                ? formatMessage({ id: 'Preview' })
                                : formatMessage({ id: 'Send' })}
                          </span>
                        )}
                      </button>
                    </div>
                    {currentDocumentStep > 1 && (
                      <div className='mr-2 w-full-mobile'>
                        <button
                          onClick={prevStep}
                          type='button'
                          className='btn btn-lg btn-light-secondary text-muted me-3 w-full-mobile mb-3 mb-md-0 custom-button-property'
                        >
                          {formatMessage({ id: 'Back' })}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='d-md-flex justify-content-md-end flex-wrap flex-nowrap w-full-mobile'>
                    {currentDocumentStep > 1 && (
                      <div className='mr-2 w-full-mobile'>
                        <button
                          onClick={prevStep}
                          type='button'
                          className='btn btn-lg btn-light-secondary text-muted me-3 w-full-mobile mb-3 mb-md-0 custom-button-property'
                        >
                          {formatMessage({ id: 'Back' })}
                        </button>
                      </div>
                    )}
                    <div className='w-full-mobile'>
                      <button
                        type='button'
                        className='btn btn-lg text-white bg-primary w-full-mobile custom-button-property'
                        disabled={!formik.isValid || loading || isInValidTemplate}
                        onClick={() => {
                          nextStep(formik)
                        }}
                      >
                        {loading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label'>
                            {currentDocumentStep === 1 || currentDocumentStep === 2
                              ? formatMessage({ id: 'Next' })
                              : currentDocumentStep === 3
                                ? formatMessage({ id: 'Preview' })
                                : formatMessage({ id: 'Send' })}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal >
  )
}

export default CreateDocumentModal
